import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail, Send, XCircle, Clock, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { cn } from '@/utils/cn'

interface EmailTemplate {
  id: string
  nome: string
  assunto: string
  categoria: string
}

interface EmailSequence {
  id: string
  leadId: string
  templateId: string
  status: string
  enviadoEm: string | null
  erro: string | null
  createdAt: string
  template: EmailTemplate
}

interface EmailSequencePanelProps {
  leadId: string
  leadEmail?: string | null
}

const statusConfig: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pendente: { label: 'Pendente', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', icon: Clock },
  enviado: { label: 'Enviado', color: 'text-green-400 bg-green-500/10 border-green-500/20', icon: CheckCircle2 },
  falhou: { label: 'Falhou', color: 'text-red-400 bg-red-500/10 border-red-500/20', icon: AlertCircle },
  cancelado: { label: 'Cancelado', color: 'text-text-muted bg-white/[0.04] border-white/[0.06]', icon: XCircle },
}

export function EmailSequencePanel({ leadId, leadEmail }: EmailSequencePanelProps) {
  const [sequences, setSequences] = useState<EmailSequence[]>([])
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [showSend, setShowSend] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [sending, setSending] = useState(false)

  const fetchSequences = useCallback(async () => {
    try {
      const { data } = await api.get(`/email/sequences?leadId=${leadId}`)
      setSequences(data.data ?? [])
    } catch {
      toast.error('Erro ao carregar sequências')
    } finally {
      setLoading(false)
    }
  }, [leadId])

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/email/templates', { params: { setor: 'bdr' } })
      setTemplates(data.data ?? [])
    } catch {
      // Silently fail - templates list is optional
    }
  }, [])

  useEffect(() => {
    fetchSequences()
    fetchTemplates()
  }, [fetchSequences, fetchTemplates])

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Selecione um template')
      return
    }

    setSending(true)
    try {
      await api.post('/email/sequences/send', {
        leadId,
        templateId: selectedTemplate,
      })
      toast.success('Email enviado com sucesso')
      setShowSend(false)
      setSelectedTemplate('')
      fetchSequences()
    } catch {
      toast.error('Erro ao enviar email')
    } finally {
      setSending(false)
    }
  }

  const handleCancel = async (id: string) => {
    try {
      await api.patch(`/email/sequences/${id}/cancel`)
      toast.success('Sequência cancelada')
      fetchSequences()
    } catch {
      toast.error('Erro ao cancelar sequência')
    }
  }

  return (
    <div className='space-y-3'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Mail size={14} className='text-accent' />
          <span className='text-xs font-bold text-text-primary uppercase tracking-wider'>
            Emails
          </span>
          {sequences.length > 0 && (
            <span className='px-1.5 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-bold'>
              {sequences.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowSend(!showSend)}
          className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-accent/10 text-accent hover:bg-accent/20 transition-all min-h-[44px]'
        >
          <Send size={12} />
          Enviar
        </button>
      </div>

      {/* Send Form */}
      <AnimatePresence>
        {showSend && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='overflow-hidden'
          >
            <div className='glass-card p-3 space-y-2'>
              {leadEmail && (
                <p className='text-[10px] text-text-muted'>
                  Enviar para: <span className='text-text-primary font-medium'>{leadEmail}</span>
                </p>
              )}
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className='w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2 text-sm outline-none focus:border-accent transition-colors min-h-[44px] appearance-none'
              >
                <option value=''>Selecione um template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome} — {t.assunto}
                  </option>
                ))}
              </select>
              <div className='flex gap-2'>
                <button
                  onClick={handleSend}
                  disabled={sending || !selectedTemplate}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all min-h-[44px]',
                    sending || !selectedTemplate
                      ? 'bg-accent/50 text-white/50 cursor-not-allowed'
                      : 'bg-accent text-white hover:brightness-110'
                  )}
                >
                  {sending ? (
                    <span className='w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                  ) : (
                    <Send size={12} />
                  )}
                  Enviar Agora
                </button>
                <button
                  onClick={() => {
                    setShowSend(false)
                    setSelectedTemplate('')
                  }}
                  className='px-3 py-2 rounded-lg text-xs font-bold text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-all min-h-[44px]'
                >
                  Cancelar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sequences List */}
      {loading ? (
        <div className='flex items-center justify-center py-6'>
          <div className='w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin' />
        </div>
      ) : sequences.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-6 text-text-muted'>
          <Mail size={24} className='mb-2 opacity-30' />
          <p className='text-xs'>Nenhum email enviado</p>
        </div>
      ) : (
        <div className='space-y-2'>
          <AnimatePresence>
            {sequences.map((seq) => {
              const cfg = statusConfig[seq.status] ?? statusConfig.pendente
              const Icon = cfg.icon

              return (
                <motion.div
                  key={seq.id}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className='glass-card p-3 space-y-1.5'
                >
                  <div className='flex items-start justify-between gap-2'>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm text-text-primary font-medium truncate'>
                        {seq.template.assunto}
                      </p>
                      <p className='text-[11px] text-text-muted truncate'>
                        {seq.template.nome}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0',
                        cfg.color
                      )}
                    >
                      <Icon size={10} />
                      {cfg.label}
                    </span>
                  </div>

                  {seq.enviadoEm && (
                    <p className='text-[10px] text-text-muted flex items-center gap-1'>
                      <Clock size={10} />
                      {new Date(seq.enviadoEm).toLocaleString('pt-BR')}
                    </p>
                  )}

                  {seq.erro && (
                    <p className='text-[10px] text-red-400 bg-red-500/5 px-2 py-1 rounded'>
                      {seq.erro}
                    </p>
                  )}

                  {seq.status === 'pendente' && (
                    <button
                      onClick={() => handleCancel(seq.id)}
                      className='flex items-center gap-1 text-[10px] text-text-muted hover:text-red-400 transition-colors'
                    >
                      <XCircle size={10} />
                      Cancelar
                    </button>
                  )}
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
